import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';

import { environment as env } from '../../environments/environment';

import { ICompany } from '../shared/declarations/company';
import { IPasswordRequest } from '../shared/declarations/password-request';
import { IBillingInfo } from '../shared/declarations/billing-info';
import { ICalendar } from '../shared/declarations/calendar';
import { IOrderFilter } from '../shared/declarations/order';
import { IDateRange } from '../shared/declarations/date';
import { IPromoCode } from '../shared/declarations/promo-code';
import { formatDateFilter } from '../shared/helpers/utils';
import { OrderStatus } from '../shared/enums/order-status.enum';
import { map } from 'rxjs/operators';

interface IPinRequest {
  pin: string;
}

@Injectable({
  providedIn: 'root'
})
export class CompanyService {

  onUpdateRestaurant: BehaviorSubject<boolean>;
  constructor(private http: HttpClient) {
    this.onUpdateRestaurant = new BehaviorSubject(false);
  }

  getSettings(companyId: number): Observable<any> {
    // the companyId will be passed by a header for some reason
    return this.http.post(`${env.apiV2Url}/admin/company/find-company-with-restaurants-and-products-by-id`, { companyId: companyId }).pipe(
      map((response: any) => {
        return response.data;
      })
    );
  }

  listCompanies(): Observable<any> {
    return this.http.get(`${env.apiV2Url}/admin/company/list-companies`).pipe(
      map((response: any) => {
        return {
          companies: response.data.companies
        }
      })
    );
  }

  listStores(): Observable<any> {
    return this.http.get(`${env.apiV2Url}/admin/store/all`).pipe(
      map((response: any) => {
        return {
          restaurants: response.data.stores
        }
      })
    );
  }

  getManagerData(): Observable<any> {
    return this.http.get(`${env.apiV2Url}/admin/manager/manager-data`).pipe(
      map((result: { data: { manager: any } }) => {
        return result.data.manager;
      })
    );
  }

  listManagers(): Observable<any> {
    return this.http.get(`${env.apiV2Url}/admin/manager/list-managers`).pipe(
      map((result: { data: any }) => {
        return result.data;
      })
    );
  }

  updateManagerDataByCompanyId(companyId: number, data: any): Observable<any> {
    return this.http.post(`${env.apiV2Url}/admin/manager/update-manager-data-by-company-id`, {
      companyId: companyId,
      ...data
    });
  }

  updateManagerLocale(locale: any): Observable<any> {
    return this.http.post(`${env.apiV2Url}/admin/manager/update-manager-locale`, { locale });
  }

  updateManagerPassword(data: IPasswordRequest) {
    return this.http.post(`${env.apiV2Url}/admin/manager/update-manager-password`, data);
  }

  boUpdateManagerPassword(managerId: number, data: IPasswordRequest) {
    return this.http.post(`${env.apiV2Url}/admin/manager/bo-update-manager-password`, {
      managerId: managerId,
      ...data
    });
  }

  getBillingInfo(companyId: number): Observable<any> {
    return this.http.post(`${env.apiV2Url}/admin/company/get-billing-info`, {companyId: companyId}).pipe(
      map((result: { data: any }) => {
        return {
          billingInfo: result.data
        }
      })
    );
  }

  updateBillingInfo(billingInfo: IBillingInfo): Observable<any> {
    return this.http.post(`${env.apiV2Url}/admin/company/update-billing-info`, billingInfo);
  }

  updateWeeklySchedule(productId: number, schedule: any[]): Observable<any> {
    return this.http.post(`${env.apiV2Url}/admin/product/update-weekly-schedule`, {productId: productId, schedule: schedule });
  }

  updateTodaySchedule(productId: number, schedule: ICalendar): Observable<any> {
    return this.http.post(`${env.apiV2Url}/admin/product/update-today-schedule`, {
      productId: productId,
      schedule: schedule
    });
  }

  createStore(data: any): Observable<any> {
    return this.http.post(`${env.apiV2Url}/admin/store/create`, data);
  }

  updateStore(placeId: number, data: FormData): Observable<any> {
    data.append('placeId', placeId.toString());
    return this.http.post(`${env.apiV2Url}/admin/store/update`, data);
  }

  activateStore(placeId: number): Observable<any> {
    return this.http.post(`${env.apiV2Url}/admin/store/activate`, {
      id: placeId,
    });
  }

  disableStore(placeId: number): Observable<any> {
    return this.http.post(`${env.apiV2Url}/admin/store/deactivate`, {
      id: placeId,
    });
  }

  createProduct(data: FormData): Observable<any> {
    return this.http.post(`${env.apiV2Url}/admin/product/create`, data);
  }

  updateProduct(id: number, data: FormData): Observable<any> {
    data.append('id', id.toString());
    return this.http.post(`${env.apiV2Url}/admin/product/update`, data);
  }

  activateCompany(companyId: number): Observable<any> {
    return this.http.post(`${env.apiV2Url}/admin/company/activate`, {
      id: companyId
    });
  }

  disableCompany(companyId: number): Observable<any> {
    return this.http.post(`${env.apiV2Url}/admin/company/deactivate`, {
      id: companyId
    });
  }

  managerConfirmOrder(data: IPinRequest): Observable<any> {
    return this.http.post(`${env.apiV2Url}/admin/order/manager-confirm`, data).pipe(
      map((result: { data: any }) => {
        return result.data;
      })
    );
  }

  findOrdersForTodayByProductId(id: number): Observable<any> {
    return this.http.post(`${env.apiV2Url}/admin/product/find-orders-for-today-by-id`, {id: id}).pipe(
      map((result: { data: any }) => {
        return result.data;
      })
    );
  }

  findOrdersByCompanyId(companyId: number): Observable<any> {
    return this.http.post(`${env.apiV2Url}/admin/order/find-by-company-id`, {companyId: companyId}).pipe(
      map((result: { data: any }) => {
        return result.data;
      })
    );
  }

  adminListOrders(): Observable<any> {
    return this.http.get(`${env.apiV2Url}/admin/order/admin-all-orders`).pipe(
      map((result: { data: any }) => {
        return result.data;
      })
    );
  }

  managerCancelOrder(orderId: number): Observable<any> {
    return this.http.post(`${env.apiV2Url}/admin/order/manager-cancel`, { id: orderId });
  }

  /** Income */

  listPromoCodes(): Observable<any> {
    return this.http.get(`${env.apiV2Url}/admin/promo-code/all`).pipe(
      map((result: { data: { promoCodes: any } }) => {
        return result.data;
      })
    );
  }

  createPromoCode(data: IPromoCode): Observable<any> {
    return this.http.post(`${env.apiV2Url}/admin/promo-code/create`, data);
  }

  editPromoCode(data: IPromoCode): Observable<any> {
    return this.http.post(`${env.apiV2Url}/admin/promo-code/edit`, data);
  }

  deletePromoCode(id: number): Observable<any> {
    return this.http.post(`${env.apiV2Url}/admin/promo-code/delete`, { id: id });
  }
}
